import { SectionNav, SectionNavItem } from "@screener/uikit/components/section-nav";
import { StatusDot } from "@screener/uikit/components/status-dot";
import { type ReactNode, useEffect, useState } from "react";
import { settingsSectionDomId } from "./settings-section";

/** Доля высоты прокручиваемой области: раздел, чей верх выше этой линии, считается текущим. */
const ACTIVE_LINE = 0.45;

export interface SettingsNavItem {
  id: string;
  label: string;
  icon: ReactNode;
  /** Точка справа: `true` — включено, `false` — выключено, `undefined` — точки нет. */
  on?: boolean;
}

function scrollParentOf(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent !== null) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/** Раздел, на котором стоит чтение: последний, чей верх выше линии; у нижнего края — последний. */
function currentSection(container: HTMLElement, ids: readonly string[]): string | null {
  const line = container.getBoundingClientRect().top + container.clientHeight * ACTIVE_LINE;
  const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 2;
  let current: string | null = null;
  for (const id of ids) {
    const element = document.getElementById(settingsSectionDomId(id));
    if (element === null) {
      continue;
    }
    if (current === null || element.getBoundingClientRect().top < line) {
      current = id;
    }
  }
  if (atBottom && container.scrollTop > 0) {
    return ids[ids.length - 1] ?? current;
  }
  return current;
}

/**
 * Навигация по разделам страницы «Настройки»: подпись, пункты с точкой
 * состояния, текущий пункт следует за прокруткой. Узкий экран её не
 * показывает: разделы идут один под другим.
 */
export function SettingsNav({ items }: { items: readonly SettingsNavItem[] }) {
  const [active, setActive] = useState<string | undefined>(items[0]?.id);
  const ids = items.map((item) => item.id).join("|");

  useEffect(() => {
    const first = document.getElementById(settingsSectionDomId(ids.split("|")[0] ?? ""));
    if (first === null) {
      return;
    }
    const container = scrollParentOf(first);
    if (container === null) {
      return;
    }
    const update = () => {
      const next = currentSection(container, ids.split("|"));
      if (next !== null) {
        setActive(next);
      }
    };
    update();
    container.addEventListener("scroll", update, { passive: true });
    // Разделы достраиваются (загрузка Telegram, таблица терминала) и сдвигают друг друга без прокрутки.
    const observer = new ResizeObserver(update);
    observer.observe(container);
    for (const id of ids.split("|")) {
      const section = document.getElementById(settingsSectionDomId(id));
      if (section !== null) {
        observer.observe(section);
      }
    }
    return () => {
      container.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [ids]);

  const goTo = (id: string) => {
    const target = document.getElementById(settingsSectionDomId(id));
    if (target === null) {
      return;
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    setActive(id);
  };

  return (
    <SectionNav
      aria-label="Разделы настроек"
      label="Разделы"
      className="sticky top-7 hidden self-start lg:block"
    >
      {items.map((item) => (
        <SectionNavItem
          key={item.id}
          icon={item.icon}
          active={item.id === active}
          indicator={
            item.on === undefined ? undefined : <StatusDot state={item.on ? "online" : "off"} />
          }
          onClick={() => goTo(item.id)}
        >
          {item.label}
        </SectionNavItem>
      ))}
    </SectionNav>
  );
}
